// src/services/usersApi.js
const DBG = (...args) => console.log("[usersApi]", ...args);

const baseUrl = "https://mydata.tablemate.net/redir.php?url=";

const PATHS = {
  fetchAll: "/endpoints/employees/fetchAllEmployees.php",
  editEmployee: "/endpoints/employees/editEmployeeByIDBESA.php",
  createEmployee: "/endpoints/employees/createEmployeeBESA.php",
  enableEmployee:
    "/endpoints/employees/updateEmployeeAccountStatusBESA.php?s=1",
  disableEmployee:
    "/endpoints/employees/updateEmployeeAccountStatusBESA.php?s=0",
  deleteEmployee: "/endpoints/employees/deleteEmployeeBESA.php",
  updateProfilePhoto: "/endpoints/employees/uploadEmployeePhotoBESA.php",
  listRoles: "/endpoints/employees/fetchRolesBESA.php",
  setEmployeePassword: "/endpoints/employees/changePasswordBESA.php",
  addEmployeeURL: "/endpoints/employees/addEmployeeBESA.php",
};

function buildOrigin(tenantServerUrl) {
  if (!tenantServerUrl) {
    throw new Error("usersApi: tenantServerUrl is required");
  }
  try{
    return tenantServerUrl.replace(/\/$/, "") + "/tablemate";
  }catch(ex){
    console.log("ERRRRPRR "+JSON.stringify(tenantServerUrl))
return tenantServerUrl + "/tablemate";
  }
  
}

const url = (tenantServerUrl, p) =>
  baseUrl + encodeURIComponent(buildOrigin(tenantServerUrl) + p);

async function getJSON(u) {
  const r = await fetch(u, { headers: { Accept: "application/json" } });
  const t = await r.text();
  if (!r.ok) throw new Error(`HTTP ${r.status} ${r.statusText} ${t || ""}`.trim());
  try {
    return JSON.parse(t);
  } catch {
    return [];
  }
}

function isFailureText(text) {
  return /^failure/i.test(String(text || "").trim());
}

async function postWithRetry(u, payload, opts = {}) {
  const raw = !!opts.raw;
  DBG("POST JSON →", u, payload);
  let r = await fetch(u, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });

  if (!r.ok) {
    const form = new URLSearchParams();
    Object.entries(payload || {}).forEach(([k, v]) =>
      form.append(k, v ?? "")
    );
    DBG("POST FORM →", u, Object.fromEntries(form));
    r = await fetch(u, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    });
  }

  const text = await r.text();
  console.log("[usersApi.postWithRetry] response text:", text);
  if (!r.ok)
    throw new Error(`HTTP ${r.status} ${r.statusText} ${text || ""}`.trim());
  try {
    const parsed = JSON.parse(text);
    if (!raw) return parsed;
    const failed =
      parsed?.ok === false ||
      isFailureText(parsed?.message || parsed?.error || "");
    return { ok: !failed, data: parsed, text };
  } catch {
    if (!raw) return true;
    const trimmed = String(text || "").trim();
    return { ok: /^success$/i.test(trimmed), data: null, text: trimmed };
  }
}

const toBool = (v) => {
  const s = typeof v === "string" ? v.trim().toLowerCase() : v;
  return !(s === 0 || s === "0" || s === false || s === "false" || s == null);
};
const boolTo10 = (b) => (b ? "1" : "0");

// ---------- mapping ----------
function isSoftDeleted(e) {
  const v = e?.is_deleted;
  return v === 1 || v === "1" || v === true || v === "true";
}

function mapUsersToDB(arr) {
  // Disabled accounts (enabled = 0) stay on the list. Only soft-deleted logins are omitted.
  const employees = (arr || []).filter((e) => !isSoftDeleted(e)).map((e, i) => {
    const id = String(
      e.id ?? e.user_id ?? e.username ?? e.email ?? i + 1
    );
    const username = e.username ?? e.email ?? id;
    const display_name = e.display_name ?? e.name ?? e.fullname ?? username;
    const active =
      e.active === undefined && e.is_active === undefined
        ? "1"
        : boolTo10(toBool(e.active ?? e.is_active));
    return { id, username, display_name, active, ...e };
  });
  return { employees };
}

// ---------- state ----------
let DB = { employees: [] };

// ---------- public ----------

// Password setter (tenant-aware)
export async function setEmployeePassword(
  tenantServerUrl,
  user_id,
  username,
  password,
  bywho = localStorage.getItem("loggedUser_Username"),
  opts = {}
) {
  const { debug = false } = opts;
  const endpoint = url(tenantServerUrl, PATHS.setEmployeePassword);

  const payload = {
    user_id: String(user_id),
    username: String(username),
    password: String(password),
    bywho,
  };

  const redacted = {
    ...payload,
    password: payload.password ? "***REDACTED***" : "",
  };

  const t0 = performance.now?.() ?? Date.now();
  const group = `[usersApi] setEmployeePassword → ${username}`;
  if (debug && console.groupCollapsed) {
    console.groupCollapsed(group);
    console.log("endpoint:", endpoint);
    console.log("payload (redacted):", redacted);
  }

  let res,
    text,
    data,
    ok = false;
  try {
    res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "omit",
      cache: "no-store",
    });

    console.log("Login payload " + JSON.stringify(payload));

    const ct = res.headers.get("content-type") || "";
    text = await res.text();

    if (debug) {
      console.log("status:", res.status, res.statusText);
      console.log("content-type:", ct);
      try {
        const entries = [];
        res.headers.forEach((v, k) => entries.push([k, v]));
        console.log("response headers:", Object.fromEntries(entries));
      } catch {}
      console.log("raw body:", text);
    }

    if (ct.includes("application/json")) {
      try {
        data = JSON.parse(text);
      } catch (e) {
        if (debug) console.warn("JSON parse failed:", e);
        data = { ok: res.ok, raw: text };
      }
    } else {
      data = { ok: res.ok, raw: text };
    }

    ok = res.ok && data?.ok !== false;
    if (!ok) {
      const msg =
        data?.message || data?.error || res.statusText || "Request failed";
      throw new Error(`HTTP ${res.status} ${msg}`);
    }

    return data;
  } catch (err) {
    if (debug) console.error("error:", err);
    throw err;
  } finally {
    const t1 = performance.now?.() ?? Date.now();
    if (debug) {
      console.log("timing_ms:", Math.round(t1 - t0));
      try {
        window._tm_lastPwPayload = redacted;
      } catch {}
      if (console.groupEnd) console.groupEnd();
    }
  }
}

export async function fetchUsers(tenantServerUrl) {
  try {
    const data = await getJSON(url(tenantServerUrl, PATHS.fetchAll));
    const arr = Array.isArray(data)
      ? data
      : data?.employees || data?.users || [];
    DB = mapUsersToDB(arr);
    console.log(JSON.parse(JSON.stringify(DB)));
    return JSON.parse(JSON.stringify(DB));
  } catch (e) {
    console.warn("[usersApi] fetchUsers fallback:", e.message);
    return JSON.parse(JSON.stringify(DB));
  }
}

export async function refreshFromServer(tenantServerUrl) {
  const data = await getJSON(url(tenantServerUrl, PATHS.fetchAll));
  const arr = Array.isArray(data)
    ? data
    : data?.employees || data?.users || [];
  DB = mapUsersToDB(arr);
  return JSON.parse(JSON.stringify(DB));
}

export async function listRoles(tenantServerUrl, { logRaw = true } = {}) {

  console.log("API: "+JSON.stringify(tenantServerUrl))
  const data = await getJSON(url(tenantServerUrl, PATHS.listRoles));

  if (logRaw) {
    console.log("[listRoles] raw:", data);
  }

  const rows = Array.isArray(data) ? data : data?.roles ?? [];
  return rows.map((r) => ({
    value: r?.id,
    label: r?.role,
  }));
}

// addEmployee (tenant-aware)
export async function addEmployee(
  tenantServerUrl,
  payload,
  { debug = false } = {}
) {
  const endpoint = url(tenantServerUrl, PATHS.addEmployeeURL);
  const redacted = { ...payload, password: payload.password ? "***" : undefined };

  if (debug && console.groupCollapsed) {
    console.groupCollapsed("[usersApi] addEmployee");
    console.log("endpoint:", endpoint);
    console.log("payload (redacted):", redacted);
  }

  let res,
    text,
    data;
  try {
    res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "omit",
      cache: "no-store",
    });
    text = await res.text();
    const ct = res.headers.get("content-type") || "";
    data = ct.includes("application/json")
      ? JSON.parse(text)
      : { raw: text, ok: res.ok };

    if (!res.ok || data?.ok === false) {
      const msg =
        data?.message || data?.error || res.statusText || "Add failed";
      throw new Error(`HTTP ${res.status} ${msg}`);
    }
    return data?.employee || data;
  } finally {
    if (debug && console.groupEnd) console.groupEnd();
  }
}

export async function editEmployee(tenantServerUrl, a, b) {
  // overload:
  // editEmployee(tenantServerUrl, payloadObj)
  // editEmployee(tenantServerUrl, id, partialUpdates)
  let payload = null;

  if (a && typeof a === "object" && !b) {
    payload = { ...a };
  } else if (a && b && typeof b === "object") {
    const id = a;
    const row = DB.employees.find(
      (x) => String(x.id) === String(id)
    );
    const username = b.username ?? row?.username ?? b.email ?? "";
    const name =
      b.name ??
      b.display_name ??
      row?.display_name ??
      row?.name ??
      "";

    payload = {
      id: String(id),
      username,
      name,
      email: b.email ?? row?.email ?? "",
      contact: b.contact ?? b.phone ?? row?.phone ?? row?.contact ?? "",
      gender: b.gender ?? row?.gender ?? "",
      nation: b.nation ?? row?.nation ?? row?.nationality ?? "",
      physical:
        b.physical ?? row?.physical ?? row?.physical_address ?? "",
      post:
        b.post ??
        b.postal_addr ??
        row?.postal_addr ??
        row?.postal_address ??
        row?.address ??
        "",
      position: b.position ?? row?.position ?? "",
      experience: b.experience ?? row?.experience ?? "",
      disabilities: b.disabilities ?? row?.disabilities ?? "",
      comments: b.comments ?? row?.comments ?? "",
      bywho:
        b.bywho || localStorage.getItem("loggedUser_Username") || "admin",
      role_id: b.role_id ?? row?.role_id,
    };
  } else {
    throw new Error("editEmployee: invalid arguments");
  }

  if (payload.active !== undefined)
    payload.active = boolTo10(toBool(payload.active));
  if (payload.is_active !== undefined)
    payload.is_active = boolTo10(toBool(payload.is_active));

  let res = null;
  try {
    res = await postWithRetry(
      url(tenantServerUrl, PATHS.editEmployee),
      payload
    );
    console.log(
      "[usersApi.editEmployee] payload →",
      payload,
      "response →",
      res
    );
  } catch (e) {
    console.warn("[usersApi] editEmployee remote failed:", e.message);
  }

  const row = DB.employees.find(
    (x) => String(x.id) === String(payload.id)
  );
  if (!row) return res || payload;

  Object.assign(row, {
    id: payload.id,
    username: payload.username,
    name: payload.name,
    display_name: payload.name,
    email: payload.email,
    contact: payload.contact,
    phone: payload.contact,
    position: payload.position,
    experience: payload.experience,
    disabilities: payload.disabilities,
    comments: payload.comments,
    nationality: payload.nation,
    nation: payload.nation,
    postal_addr: payload.post,
    physical_address: payload.physical,
  });

  return row;
}

export const enableEmployee = (tenantServerUrl, id, bywho = "admin") =>
  postWithRetry(url(tenantServerUrl, PATHS.enableEmployee), { id, bywho });

export const disableEmployee = (tenantServerUrl, id, bywho = "admin") =>
  postWithRetry(url(tenantServerUrl, PATHS.disableEmployee), {
    id,
    bywho,
  });

export async function deleteEmployee(
  tenantServerUrl,
  id,
  bywho = "admin",
  extras = {}
) {
  const extra = typeof extras === "string" ? { username: extras } : extras || {};
  const username = String(extra.username || "").trim();
  // employees.id lives on info.user_id. info.id must not be sent as `id`:
  // older deleteEmployeeBESA.php updates employees WHERE id = that value.
  const employeePkNum = Number(extra.user_id ?? extra.userId);
  const employeePk =
    Number.isFinite(employeePkNum) && employeePkNum > 0
      ? String(employeePkNum)
      : "";
  const payload = { bywho: bywho || "admin" };
  if (username) payload.username = username;
  if (employeePk) {
    payload.id = employeePk;
    payload.user_id = employeePk;
  } else if (!username && id != null && String(id) !== "") {
    payload.id = String(id);
  }

  if (!payload.username && !payload.id && !payload.user_id) {
    throw new Error("Delete failed: missing username and employee id");
  }

  const res = await postWithRetry(
    url(tenantServerUrl, PATHS.deleteEmployee),
    payload,
    { raw: true }
  );

  if (!res?.ok) {
    const msg =
      res?.data?.message ||
      res?.data?.error ||
      res?.text ||
      "Delete failed";
    throw new Error(msg);
  }

  DB.employees = DB.employees.filter(
    (e) => String(e.id) !== String(id)
  );
  return res.data ?? res.text ?? true;
}

// utils: pretty-print a FormData without consuming the file stream
function dumpFormData(fd) {
  const out = [];
  for (const [k, v] of fd.entries()) {
    if (v instanceof File) {
      out.push({
        key: k,
        type: "File",
        name: v.name,
        size: v.size,
        mime: v.type,
      });
    } else {
      out.push({ key: k, value: v });
    }
  }
  return out;
}

export async function updateProfilePhoto(
  tenantServerUrl,
  id,
  file,
  bywho = "admin",
  opts = {}
) {
  const {
    username = null,
    debug = true,
    endpoint, // optional override
    extra = {},
    signal,
  } = opts;

  if (!(file instanceof File)) {
    throw new Error('updateProfilePhoto: "file" must be a File');
  }

  const effectiveEndpoint =
    endpoint || url(tenantServerUrl, PATHS.updateProfilePhoto);

  const fd = new FormData();
  fd.append("id", String(id));
  fd.append("bywho", String(bywho));
  if (username) fd.append("username", String(username));
  fd.append("photo", file, file.name);
  for (const [k, v] of Object.entries(extra)) {
    fd.append(k, String(v));
  }

  const t0 = performance.now();

  if (debug) {
    console.groupCollapsed(
      "%c[updateProfilePhoto] → request",
      "color:#0a7"
    );
    console.log("endpoint:", effectiveEndpoint);
    console.log("method:", "POST (multipart/form-data)");
    console.table(dumpFormData(fd));
    console.log("file details:", {
      name: file.name,
      size: file.size,
      type: file.type,
    });
    console.groupEnd();
  }

  let res,
    text;
  try {
    res = await fetch(effectiveEndpoint, { method: "POST", body: fd, signal });
  } catch (e) {
    if (debug) {
      console.group(
        "%c[updateProfilePhoto] ✖ network error",
        "color:#c00"
      );
      console.error(e);
      console.groupEnd();
    }
    throw e;
  }

  const durMs = Math.round(performance.now() - t0);

  try {
    text = await res.text();
  } catch {
    text = "";
  }

  let parsed = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    // keep as text
  }

  if (debug) {
    const headersObj = {};
    res.headers.forEach((v, k) => {
      headersObj[k] = v;
    });

    console.groupCollapsed(
      `${
        res.ok
          ? "%c[updateProfilePhoto] ✓ response"
          : "%c[updateProfilePhoto] ⚠ response"
      }`,
      res.ok ? "color:#0a7" : "color:#d98400"
    );
    console.log(
      "status:",
      res.status,
      res.statusText,
      `(${durMs} ms)`
    );
    console.table(headersObj);
    if (parsed) {
      console.log("body (json):", parsed);
    } else {
      console.log(
        "body (text):",
        text?.slice(0, 2000) ?? "(empty)"
      );
    }
    if (
      headersObj["content-type"] &&
      !headersObj["content-type"].includes("application/json")
    ) {
      console.warn(
        "Note: server did not return JSON content-type."
      );
    }
    if (res.status === 413) {
      console.warn(
        "413 Payload Too Large: increase PHP upload_max_filesize / post_max_size."
      );
    }
    if (res.status === 415) {
      console.warn(
        "415 Unsupported Media Type: server likely expects multipart/form-data."
      );
    }
    console.groupEnd();
  }

  if (!res.ok) {
    const snippet = text ? ` — ${text.slice(0, 300)}` : "";
    throw new Error(
      `updateProfilePhoto failed: HTTP ${res.status} ${res.statusText}${snippet}`
    );
  }

  if (parsed?.php_limits) {
    if (debug) console.info("[updateProfilePhoto] php_limits:", parsed.php_limits);
  }

  return parsed ?? true;
}

// import/export for manager toolbar (local only)
export async function importUsers(jsonText) {
  const parsed = JSON.parse(jsonText);
  const arr = Array.isArray(parsed?.employees)
    ? parsed.employees
    : Array.isArray(parsed)
    ? parsed
    : null;
  if (!arr)
    throw new Error("Invalid format: expected array or { employees: [...] }");
  DB = mapUsersToDB(arr);
  return JSON.parse(JSON.stringify(DB));
}

export async function exportUsers() {
  return JSON.stringify(DB, null, 2);
}

// delete profile photo (tenant-aware)
export async function deleteProfilePhoto(
  tenantServerUrl,
  id,
  bywho = "admin"
) {
  const PATH = "/endpoints/employees/deleteProfilePhotoBESA.php";
  const u = url(tenantServerUrl, PATH);
  return postWithRetry(u, { id, bywho });
}

// Back-compat aliases so UsersManager can reuse Toolbar props
export const fetchMenu = (tenantServerUrl) => fetchUsers(tenantServerUrl);
export const addCategory = (tenantServerUrl, x) =>
  addEmployee(
    tenantServerUrl,
    typeof x === "string" ? { username: x } : x
  );
export const editCategory = (tenantServerUrl, id, newNameOrObj) => {
  if (typeof newNameOrObj === "string") {
    return editEmployee(tenantServerUrl, id, {
      display_name: newNameOrObj,
      name: newNameOrObj,
    });
  }
  return editEmployee(tenantServerUrl, id, newNameOrObj);
};
export const addSubcategory = async () => {
  throw new Error("Not applicable for employees");
};
export const importMenu = importUsers;
export const exportMenu = exportUsers;
